/**
 * Socket.io provider for real-time communication
 * Manages WebSocket connection and organization room subscription
 */
import { SocketContext, type SocketContextType } from '@/contexts/socket.context'
import { useActiveOrg } from '@/hooks/use-active-org'
import * as React from 'react'
import { io, type Socket } from 'socket.io-client'

const AUTH_RETRY_DELAY_MS = 3000

interface SocketProviderProps {
  children: React.ReactNode
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = React.useState<Socket | null>(null)
  const [isConnected, setIsConnected] = React.useState(false)
  const [connectionError, setConnectionError] = React.useState<string | null>(null)
  const { activeOrg } = useActiveOrg()
  const previousOrgIdRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    const socketUrl = import.meta.env.VITE_SOCKET_URL

    if (!socketUrl) {
      console.warn('Socket URL not configured (set VITE_SOCKET_URL)')
      return
    }

    let authRetryTimer: ReturnType<typeof setTimeout> | null = null

    const newSocket = io(socketUrl, {
      path: '/socket.io/',
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      upgrade: true,
      rememberUpgrade: true,
      withCredentials: true,
      timeout: 20000,
      // Clerk tokens are short-lived, so fetch a fresh one for every
      // connection attempt instead of capturing a token once at mount -
      // otherwise reconnects after expiry are rejected by the server.
      auth: (cb) => {
        const tokenPromise = typeof window !== 'undefined' ? window.Clerk?.session?.getToken() : undefined
        if (!tokenPromise) {
          cb({ token: null })
          return
        }
        tokenPromise
          .then((token) => cb({ token }))
          .catch((error) => {
            console.warn('Failed to get Clerk session token:', error)
            cb({ token: null })
          })
      },
    })

    newSocket.on('connect', () => {
      setIsConnected(true)
      setConnectionError(null)
      if (import.meta.env.DEV) console.info('🔌 Socket connected')
    })

    newSocket.on('disconnect', (reason) => {
      if (import.meta.env.DEV) console.info('🔌 Socket disconnected:', reason)
      // Server-side room membership is lost on disconnect; clear the ref so
      // the join effect re-joins the active org room after reconnecting.
      previousOrgIdRef.current = null
      setIsConnected(false)
    })

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message)
      setConnectionError(error.message)
      setIsConnected(false)
      // When the server rejects the handshake (e.g. expired token) socket.io
      // does not reconnect automatically (`active` is false) - retry manually
      // so the auth callback can fetch a fresh token.
      if (!newSocket.active) {
        if (authRetryTimer) clearTimeout(authRetryTimer)
        authRetryTimer = setTimeout(() => newSocket.connect(), AUTH_RETRY_DELAY_MS)
      }
    })

    setSocket(newSocket)

    return () => {
      if (authRetryTimer) clearTimeout(authRetryTimer)
      newSocket.removeAllListeners()
      newSocket.disconnect()
      previousOrgIdRef.current = null
      setSocket(null)
      setIsConnected(false)
    }
  }, [])

  // Join organisation room when socket and active org are ready. The server
  // derives the user from the authenticated connection and verifies org
  // membership before joining the room.
  React.useEffect(() => {
    if (!socket || !isConnected || !activeOrg?.id) return

    const previousOrgId = previousOrgIdRef.current

    if (previousOrgId && previousOrgId !== activeOrg.id) socket.emit('leave:organisation', previousOrgId)

    if (activeOrg.id !== previousOrgId) {
      socket.emit('join:organisation', { organisationId: activeOrg.id })
      previousOrgIdRef.current = activeOrg.id
      if (import.meta.env.DEV) console.info(`🔌 Socket joined organisation room: ${activeOrg.id}`)
    }
  }, [socket, isConnected, activeOrg?.id])

  const value: SocketContextType = React.useMemo(
    () => ({
      socket,
      isConnected,
      connectionError,
    }),
    [socket, isConnected, connectionError]
  )

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
}
